import { MEDIA_FIT_OPTIONS, type MediaFitMode } from '@cornerstone/shared';
import { cn } from '../utils/cn';

// =============================================================================
// FitModeRadio — 화면 맞춤(맞춤·채우기·늘이기) 아이콘 라디오. 2026-09-14 관리자 UX 요청 P-2.
//
// 텍스트 셀렉트 대신 아이콘 세 개로 고르고, 설명은 브라우저 기본 툴팁(title)으로 보인다.
// 접근성 이름은 기존 라벨('맞춤 (Contain)' 등)을 그대로 쓴다.
//
// 3차(대교): 클릭 뒤 툴팁이 계속 떠 있던 건 focus-within 때문. 4차: 직접 그린 툴팁은 행·패널의
// overflow에 잘려 다른 메뉴 위에 떠 보였다 → 커스텀 툴팁을 없애고 title 속성만 남긴다(브라우저가
// 잘림 없이, 호버 시에만 보여준다). '슬라이드 설정 따름' 선택지는 없다 — 공지 항목은 호출부가
// 슬라이드에 저장된 맞춤을 기본 선택으로 넘긴다.
// =============================================================================

const OPTIONS = MEDIA_FIT_OPTIONS.map((option) => ({ value: option.value, title: option.label, description: option.description }));

export function FitModeRadio({
  value,
  onChange,
  label,
  disabled = false,
  size = 'sm',
  className,
}: {
  value: MediaFitMode;
  onChange: (next: MediaFitMode) => void;
  /** 라디오 그룹 접근성 이름 (예: "로비 영상 화면 맞춤"). */
  label: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}): JSX.Element {
  const box = size === 'md' ? 'h-14 w-16' : 'h-8 w-10';
  return (
    <div role="radiogroup" aria-label={label} className={cn('flex items-center gap-1', className)} onPointerDown={(event) => event.stopPropagation()}>
      {OPTIONS.map((option) => (
        <label
          key={option.value}
          title={`${option.title} — ${option.description}`}
          className={cn(
            'flex cursor-pointer items-center justify-center rounded border transition-colors',
            box,
            value === option.value
              ? 'border-accent bg-accent-soft text-accent'
              : 'border-line bg-panel-2 text-text-dim hover:border-text-dimmer hover:text-text',
            disabled && 'cursor-not-allowed opacity-45',
          )}
        >
          <input
            type="radio"
            name={`fit-${label}`}
            value={option.value}
            aria-label={option.title}
            checked={value === option.value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
            className="sr-only"
          />
          <FitModeIcon mode={option.value} small={size === 'sm'} />
        </label>
      ))}
    </div>
  );
}

/** 바깥 사각형 = 화면, 안쪽 도형 = 미디어. */
export function FitModeIcon({ mode, small = false }: { mode: MediaFitMode; small?: boolean }): JSX.Element {
  const w = small ? 24 : 36; const h = small ? 16 : 24;
  return (
    <svg width={w} height={h} viewBox="0 0 36 24" aria-hidden="true">
      <rect x="1" y="1" width="34" height="22" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      {mode === 'contain' && <rect x="9" y="4" width="18" height="16" rx="1" fill="currentColor" opacity="0.85" />}
      {mode === 'cover' && <>
        <rect x="1" y="1" width="34" height="22" rx="2" fill="currentColor" opacity="0.85" />
        <rect x="-6" y="-2" width="7" height="28" fill="currentColor" opacity="0.35" />
        <rect x="35" y="-2" width="7" height="28" fill="currentColor" opacity="0.35" />
      </>}
      {mode === 'stretch' && <>
        <rect x="3" y="3" width="30" height="18" rx="1" fill="currentColor" opacity="0.85" />
        <path d="M8 12h6m14 0h-6" stroke="#fff" strokeWidth="1.5" />
        <path d="M11 9l3 3-3 3M25 9l-3 3 3 3" fill="none" stroke="#fff" strokeWidth="1.5" />
      </>}
    </svg>
  );
}
